import { ColumnInstance, Row, useTable } from 'react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CopyPartyLedgersTable } from './CopyPartyLedgersTable';
import { CustomHeaderColumn } from './columns/CustomHeaderColumn';
import { CustomRow } from './columns/PartyRow';
import { KTCardBody } from '../../../../../../_metronic/helpers';
import { PartyLedgerColumns } from './columns/_columns';
import { PartyLedgerListLoading } from '../components/loading/PartyLedgersListLoading';
import { PartyLedgerListPagination } from '../components/pagination/PartyLedgersListPagination';
import { PartyLedger } from '../core/_models';
import { toggleChecked, setReceiverId } from 'src/store/balanceShow/actions';
import { constants } from 'src/utils/const';
import { SET_CHECKED_FALSE } from 'src/store/balanceShow/types';
import { resetFormFields } from 'src/store/formReset/actions';
import { useQueryRequest } from '../core/QueryRequestProvider';
import {
  useQueryResponse,
  useQueryResponseDataLedger,
  useQueryResponseDataMessage,
  useQueryResponseLoading,
  useQueryResponseDataLedgerWithThree,
} from '../core/QueryResponseProvider';

interface RowData {
  id: number;
  sender_id: number;
  receiver_id: number;
  amount: number;
  remarks: string;
  date: string;
  credit?: number;
  debit?: number;
  message_id?: string;
  [key: string]: any;
}

interface PartyLedgerTableProps {
  selectedRows: RowData[];
  setSelectedRows: (rows: RowData[]) => void;
  isCheckboxChecked: boolean;
  setIsCheckboxChecked: (value: boolean) => void;
  senderId: any;
  prevSender: any;
  setPrevSender: (value: any) => void;
  partyId: any;
  setPartyId: (value: any) => void;
  receiverId: any;
  setReceiverId: (value: any) => void;
}

const PartyLedgerTable: React.FC<PartyLedgerTableProps> = ({
  senderId,
  prevSender,
  setPrevSender,
  selectedRows,
  setSelectedRows,
  setPartyId,
  receiverId,
  setReceiverId,
  isCheckboxChecked,
  setIsCheckboxChecked
}) => {
  const PartyLedgerMessage = useQueryResponseDataLedgerWithThree();
  const PartyLedgers = useQueryResponseDataLedger();
  const { refetch } = useQueryResponse();
  const isLoading = useQueryResponseLoading();
  const { state, updateState } = useQueryRequest() as any;
  const dispatch = useDispatch();
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const dataMessage = useMemo(() => PartyLedgerMessage, [PartyLedgerMessage, isLoading]);
  const data = useMemo(() => {
    return (
      PartyLedgers?.map((row: RowData) => {
        if (row.message_id === 'Closing Balance') {
          const debitAmount = row.debit ?? 0;
          return {
            ...row,
            credit: debitAmount > 0 ? debitAmount * -1 : row.credit,
            debit: 0,
          };
        }
        return row;
      }) || []
    );
  }, [PartyLedgers, isLoading]);

  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(
    () =>
      state?.filter?.radioValue == '1'
        ? PartyLedgerColumns.filter((x) => x.id !== 'actions')
        : PartyLedgerColumns.map(column => {
            if (column.id === 'nselect') {
              return {
                ...column,
                Header: (props: any) => {
                  if (typeof column.Header === 'function') {
                    const HeaderComponent = column.Header as React.ComponentType<any>;
                    return <HeaderComponent {...props} isCheckboxChecked={isCheckboxChecked} setIsCheckboxChecked={setIsCheckboxChecked} setSelectedRows={setSelectedRows} />;
                  }
                  return column.Header;
                },
              };
            }
            return column;
          }),
    [isLoading, selectedRows, isCheckboxChecked, setIsCheckboxChecked, setSelectedRows]
  ) as any;

  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } = useTable({
    columns,
    data,
  });

  // Reset rowRefs when rows change
  useEffect(() => {
    rowRefs.current = rowRefs.current.slice(0, rows.length);
  }, [rows]);

  // Handle row click
  const handleRowClick = async (
    row: Row<PartyLedger>,
    event: React.MouseEvent<HTMLTableRowElement>,
    index: number
  ) => {
    // Untick the checkbox if it is checked
    if (isCheckboxChecked) {
      setIsCheckboxChecked(!isCheckboxChecked);
    }
    const target = event.target as HTMLElement;
    const cell = target.closest('td');
    if (
      row.original.message_id === 'Opening Balance' ||
      row.original.message_id === 'Current Balance' ||
      row.original.message_id === 'Closing Balance' ||
      row.original.message_id === 'Difference'
    ) {
      return;
    }

    if (cell) {
      const cellIndex = cell.cellIndex;
      const column = headers[cellIndex];

      if (column.id === 'receiver') {
        const fromDate = state?.filter?.['filter[fromdate]'];
        const toDate = state?.filter?.['filter[todate]'];
        dispatch({ type: SET_CHECKED_FALSE });
        dispatch(resetFormFields());
        await updateState({
          filter: {
            orderBy: 'created_date ASC',
            'filter[fromdate]': fromDate,
            'filter[todate]': toDate,
            'filter[limit]': 100,
            'filter[offset]': 0,
          },
          uuid: row.original.receiver_party_id,
        });

        setSelectedRows([]);
        setPartyId(row.original.receiver_party_id);
        setReceiverId(null);
        return;
      }
    }

    // Handle shift+click for range selection
    if (event.shiftKey && lastSelectedIndex !== null) {
      event.preventDefault();

      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const newSelectedRows = [...selectedRows];
      const rangeRows = rows.slice(start, end + 1).map((row) => row.original as RowData);

      rangeRows.forEach((row) => {
        if (!newSelectedRows.some((selected) => selected.id === row.id)) {
          newSelectedRows.push(row);
        }
      });

      setSelectedRows(newSelectedRows);
      setLastSelectedIndex(index);
      return;
    }

    // Default row selection logic
    const rowData = row.original as RowData;
    const isSelected = selectedRows.some((selected: any) => selected.id === rowData.id);

    if (isSelected) {
      setSelectedRows(selectedRows.filter((selected: any) => selected.id !== rowData.id));
    } else if (senderId != prevSender) {
      setSelectedRows([]);
      setPrevSender(senderId);
    } else {
      setSelectedRows([...selectedRows, rowData]);
    }

    setLastSelectedIndex(index);

    // Focus the table container when clicking a row
    if (tableContainerRef.current) {
      tableContainerRef.current.focus();
    }
  };

  // const handleSelectAll = () => {
  //   // Filter out balance rows
  //   const dataRows = rows.filter(row => {
  //     const rowData = row.original as RowData;
  //     return !['Opening Balance', 'Current Balance', 'Closing Balance', 'Difference'].includes(rowData.message_id || '');
  //   });
    
  //   if (selectedRows.length === dataRows.length) {
  //     // Deselect all rows if they are all selected
  //     setSelectedRows([]);
  //   } else {
  //     // Select all data rows if not all are selected
  //     setSelectedRows(dataRows.map((row) => row.original as RowData));
  //   }
  // };
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Ctrl+A globally
      if (event.ctrlKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setIsCheckboxChecked(!isCheckboxChecked);
        setSelectedRows([]);
        return;
      }

      // Only handle arrow keys when table has focus
      if (
        tableContainerRef.current &&
        (tableContainerRef.current.contains(document.activeElement) ||
          document.activeElement === tableContainerRef.current)
      ) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
  
          let newIndex = lastSelectedIndex ?? 0;
  
          if (event.key === 'ArrowUp' && lastSelectedIndex !== null && lastSelectedIndex > 0) {
            newIndex = lastSelectedIndex - 1;
          } else if (
            event.key === 'ArrowDown' &&
            lastSelectedIndex !== null &&
            lastSelectedIndex < rows.length - 1
          ) {
            newIndex = lastSelectedIndex + 1;
          }
  
          setLastSelectedIndex(newIndex);
          
          // Toggle selection for the new row
          const rowData = rows[newIndex].original as RowData;
          const isSelected = selectedRows.some((selected) => selected.id === rowData.id);
          
          if (isSelected) {
            setSelectedRows(selectedRows.filter((selected) => selected.id !== rowData.id));
          } else {
            setSelectedRows([...selectedRows, rowData]);
          }
          
          // Ensure the row exists and scroll it into view
          const rowElement = rowRefs.current[newIndex];
          if (rowElement) {
            setTimeout(() => {
              rowElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }, 0);
          }
        }
      }
    };
  
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lastSelectedIndex, rows, setSelectedRows, selectedRows, setIsCheckboxChecked]);
  
  const moduleName = useSelector((state: any) => state.shortCut.module_name);
  const scrollRef = useRef<any>(null);

  useEffect(() => {
    if (moduleName == 'reloadAddMessage') {
      refetch();
    }
  }, [moduleName]);

  const previousRowsLengthRef = useRef(rows.length);

  useEffect(() => {
    if (scrollRef.current) {
      const previousRowsLength = previousRowsLengthRef.current;
      if (state?.filter?.radioValue == '2' && previousRowsLength !== rows.length) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      } else if (previousRowsLength !== rows.length) {
        scrollRef.current.scrollTop = 0;
      }
      previousRowsLengthRef.current = rows.length;
    }
  }, [rows, state?.filter?.radioValue,selectedRows]);

  return (
    <KTCardBody className="p-0">
      <CopyPartyLedgersTable />
      <div
        className="table-responsive custom-table-scroll text-center"
        ref={tableContainerRef}
        tabIndex={0}
        style={{ outline: 'none' }}
      >
        <table
          id="kt_table_PartyLedger"
          className="table align-middle table-row-dashed fs-6 gy-4 table-hover dataTable custom-party-ledger-tbl no-footer"
          {...getTableProps()}
        >
          <thead className="custom-sticky-top">
            <tr className="text-center fw-bolder fs-6 text-uppercase gs-0">
              {headers.map((column: ColumnInstance<PartyLedger>) => (
                <CustomHeaderColumn 
                  key={column.id} 
                  column={column}
                  selectedRows={selectedRows}
                  rows={rows}
                />
              ))}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows?.length > 0 ? (
              rows.map((row: Row<PartyLedger>, i) => {
                prepareRow(row);

                const isSelected = selectedRows.some((selected: any) => selected.id === row.original.id);
                const isActive = i === lastSelectedIndex;
                const ok = row.original.created_at;

                const rowStyle = {
                  ...(isSelected && ok ? { color: 'red' } : {}),
                  ...(isActive ? { outline: '2px solid blue', outlineOffset: '-2px' } : {}),
                  cursor: 'pointer'
                };

                return (
                  <tr
                    ref={el => rowRefs.current[i] = el}
                    {...row.getRowProps()}
                    key={`row-${i}`}
                    onClick={(event: React.MouseEvent<HTMLTableRowElement>) => handleRowClick(row, event, i)}
                    style={rowStyle}
                  >
                    {row.cells.map((cell) => {
                      const isActionsColumn = cell.column.id === 'actions';
                      return (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            color: isSelected && ok ? 'red' : 'black',
                            textAlign: 'center',
                            userSelect: 'none',
                            border: 'none',
                          }}
                          onClick={isActionsColumn ? (e) => e.stopPropagation() : undefined}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={headers.length}>
                  <div className="d-flex text-center w-100 align-content-center justify-content-center">
                    No matching records found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <PartyLedgerListPagination />
      {isLoading && <PartyLedgerListLoading />}
    </KTCardBody>
  );
};

export { PartyLedgerTable };